import { useEffect, useState } from "react";
import { RefreshCw, Clock } from "lucide-react";
import { dashboardsApi } from "../../api/dashboards";
import { LoadingText } from "../ui";

interface Props {
  dashboardId: number;
  dashboardName: string;
}

type SnapshotTileResult = { rows?: Record<string, unknown>[]; error?: string };
type Snapshot = {
  tile_results?: Record<string, SnapshotTileResult>;
  run_at?: string;
  status?: string;
} | null;
type Tile = { id: number | string; name?: string; title?: string; width?: string };

export default function ScheduledDashboardTab({ dashboardId }: Props) {
  const [snapshot, setSnapshot] = useState<Snapshot>(null);
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function loadSnapshot() {
    setLoading(true);
    setError("");
    try {
      const [dashRes, snapRes] = await Promise.all([
        dashboardsApi.get(dashboardId),
        dashboardsApi.latestSnapshot(dashboardId),
      ]);
      setTiles(dashRes.data.tiles || []);
      setSnapshot(snapRes.data);
    } catch {
      setError("Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSnapshot(); }, [dashboardId]);

  if (loading) return <LoadingText className="p-6" />;
  if (error) return <div className="p-6 text-sm text-red-500">{error}</div>;

  const results: Record<string, SnapshotTileResult> = snapshot?.tile_results ?? {};
  const runAt = snapshot?.run_at ? new Date(snapshot.run_at).toLocaleString() : null;

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Clock size={12} />
          {runAt ? `Last updated ${runAt}` : "No data yet"}
          <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${
            snapshot?.status === "completed" ? "bg-green-50 text-green-700" :
            snapshot?.status === "partial" ? "bg-amber-50 text-amber-700" :
            "bg-gray-100 text-gray-500"
          }`}>
            {snapshot?.status ?? "pending"}
          </span>
        </div>
        <button
          onClick={loadSnapshot}
          className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-12 gap-4">
        {tiles.map((tile) => {
          const result: SnapshotTileResult = results[tile.id] ?? {};
          const rows: Record<string, unknown>[] = result.rows ?? [];
          return (
            <div
              key={tile.id}
              className={`${
                tile.width === "full" ? "col-span-12" :
                tile.width === "half" ? "col-span-6" :
                tile.width === "third" ? "col-span-4" :
                "col-span-3"
              } rounded-xl border border-gray-200 bg-white p-4`}
            >
              <p className="mb-2 text-sm font-semibold text-gray-800">{tile.name || tile.title || "Tile"}</p>
              {result.error ? (
                <p className="text-xs text-red-500">{result.error}</p>
              ) : rows.length === 0 ? (
                <p className="text-xs text-gray-400">No data</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-gray-700">
                    <thead>
                      <tr className="border-b border-gray-100">
                        {Object.keys(rows[0]).map((col) => (
                          <th key={col} className="py-1 pr-3 text-left font-medium text-gray-500">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 20).map((row, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="py-1 pr-3">{String(val ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
